import React, {useState} from 'react';

import {
    Clear as CloseIcon,
} from '@mui/icons-material';
import {Box,Button, Stack, Typography} from '@mui/material';
import {useQueryClient} from '@tanstack/react-query';

import {useTranslation} from 'hooks';
import {Create} from 'module/client/team';
import {useApiQuery as useTeamApiQuery} from 'module/client/team/useTeamApi';
import {useApiQuery as useClientApiQuery} from 'module/client/useClientApi';
import {useSettingsStore} from 'module/settings/zustand';
import {useUserUtils} from 'module/user/useUser';

import {theme} from 'theme';

const styles = {
    scrollContainer: {
        marginTop: 1,
        marginBottom: 1,
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: '0',
        height: 'auto',
        width: '100%',
        padding: '0 8px',
        borderRadius: '8px',
    },
    listContainer: {
        width: '100%',
        display: 'flex',
        padding: '0',
    },
    clientNameGroup: {
        alignItems: 'start',
        justifyContent: 'start',
        width: '100%',
    },
    // clientName: theme => ({
    //     width: '100%',
    //     height: 'auto',
    //     fontSize: '12px',
    //     color: theme.palette.lineup.textPrimary,
    //     justifyContent: 'center',
    //     alignItems: 'center',
    //     borderRadius: '8px',
    //     margin: '8px 0 0 0',
    //     background: theme.palette.grey.darker,
    //     textTransform: 'none',
    //     fontWeight: '400',
    //     position: 'relative',

    //     '&:hover': {
    //         color: theme.palette.green.lineup,
    //     }
    // }),
    teamNameGroup: {
        width: '100%',
        boxSizing: 'border-box',
        justifyContent: 'center',
        alignItems: 'center',
    },
    createTeamButton: {
        marginTop: '12px',
        background: theme => theme.palette.red.main,
        borderRadius: '8px',
    },
    client: theme => ({
        width: '100%',
        fontSize: '14px',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '10px',
        marginTop: '6px',
        borderRadius: '8px',
        background: theme.palette.grey.darker,
        textTransform: 'none',
        fontWeight: '600',
        position: 'relative',
        cursor: 'arrow',
        overflow: 'visible',
        opacity: '1',
        flexDirection: 'column',

        '&:hover': {

            background: theme.palette.grey.darker,
            '& .MuiTypography-root': {

                color: theme.palette.green.lineup,
            }
        }
    }),
    selectedClient: theme => ({
        width: '100%',
        fontSize: '14px',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        padding: '10px',
        marginTop: '23px',
        borderRadius: '8px',
        background: theme.palette.grey.darker,
        textTransform: 'none',
        fontWeight: '600',
        position: 'relative',
        cursor: 'arrow',
        overflow: 'visible',
        opacity: '1',
        flexDirection: 'column',
    }),
    selectedTeam: theme => ({
        width: '100%',
        fontSize: '14px',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        padding: '10px',
        marginTop: '23px',
        borderRadius: '8px',
        background: theme.palette.grey.darker,
        textTransform: 'none',
        fontWeight: '600',
        position: 'relative',
        cursor: 'arrow',
        overflow: 'visible',
        opacity: '1',
        flexDirection: 'column',

        '&:first-of-type': {
            marginTop: '21px',
        }
    }),
    miniTitle: {
        color: `#fff !important`,
        fontSize: '12px',
        textAlign: 'left',
        paddingRight: '20px',
    },
    title: {
        fontSize: '12px',
        textAlign: 'left',
    },
    titleActive: {
        fontSize: '14px',
        color: theme.palette.green.lineup,
    },
    notFoundContainer: {
        background: theme.palette.red.dark,
        padding: '12px',
        borderRadius: '8px',
        marginTop: '6px',
    },
    notFoundTitle: {
        fontSize: '12px',
        textAlign: 'center',
        color: theme.palette.white.main,
    },
    deleteClient: {
        zIndex: 99,
        position: 'absolute',
        top: '-15px',
        left: '50%',
        transform: 'translate(-50%)',
        color: '#fff',
        cursor: 'pointer',
        background: theme.palette.grey.darker,
        width: '30px',
        height: '30px',
        borderRadius: '15px',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    },
    deleteTeam: {
        zIndex: 99,
        position: 'absolute',
        top: '-15px',
        left: '50%',
        transform: 'translate(-50%)',
        color: '#fff',
        cursor: 'pointer',
        background: theme.palette.grey.darker,
        width: '30px',
        height: '30px',
        borderRadius: '15px',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    },
    closeIconStyle: {
        fontSize: '16px',
    },
    seperator: {
        width: '100%',
        height: '1px',
        backgroundColor: '#999',
        marginTop: '16px',
        marginBottom: '11px',
    },
    clientTitleContainer: {
        marginTop: '3px',
        marginBottom: '4px',
        textAlign: 'center',
    },
    teamTitleContainer: {
        marginTop: 0,
        marginBottom: '6px',
        textAlign: 'center',
    },
    teamTitle: {
        fontSize: '14px',
        color: '#b5b5b5',
    }
};

const Selector = ({type}) => {
    const translate = useTranslation();
    const [showCreate, setShowCreate] = useState(false);
    const {isOwner} = useUserUtils();

    const setSelectedClient = useSettingsStore(state => state.setSelectedClient);
    const setSelectedEnemyClient = useSettingsStore(state => state.setSelectedEnemyClient);
    const selectedClient = useSettingsStore(state => state.selectedClient);
    const selectedEnemyClient = useSettingsStore(state => state.selectedEnemyClient);

    const setSelectedTeam = useSettingsStore(state => state.setSelectedTeam);
    const setSelectedEnemyTeam = useSettingsStore(state => state.setSelectedEnemyTeam);
    const selectedTeam = useSettingsStore(state => state.selectedTeam);
    const selectedEnemyTeam = useSettingsStore(state => state.selectedEnemyTeam);

    const queryClient = useQueryClient();
    const clientQuery = useClientApiQuery();
    const teamQuery = useTeamApiQuery({clientId: type === 'home' ? selectedClient?.id : selectedEnemyClient?.id});
    const filteredTeams = teamQuery?.data
        .filter(t => {
            // Check if current user is a manager of this team
            const isUserManager = isOwner(t.managers);
            // filter teams that are already selected on enemy side
            const isAlreadySelected = (type === 'guest' && t.id === selectedTeam) || (type === 'home' && t.id === selectedEnemyTeam);
            // Check if there are any teams with the matching type
            const hasMatchingTypeTeams = teamQuery.data.some(team => team.type === type);
            // If there are teams with matching type, only show those, else show all teams
            const typeMatches = hasMatchingTypeTeams ? t.type === type : true;
            // Return teams that match type and are not already selected
            return typeMatches && !isAlreadySelected && isUserManager;
        });
    const filteredClients = clientQuery.data?.filter(c => {
        // filter client for current type that are already selected
        const isAlreadySelected = (type === 'home' && c.id === selectedClient?.id) || (type !== 'home' && c.id === selectedEnemyClient?.id);
        return !isAlreadySelected;
    });
    const selectTeam = (team) => {
        queryClient.invalidateQueries({queryKey: ['user-team', team.id]});
        if (type === 'home') {
            setSelectedTeam(team.id);
        } else if (type === 'guest') {
            setSelectedEnemyTeam(team.id);
        }

        // closePanel();
    };

    const onSaveTeam = async (newTeam) => {
        setShowCreate(false);
        await teamQuery.refetch();
        selectTeam(newTeam);
    };

    return (
        <Stack direction="column" sx={styles.scrollContainer}>

            {/* type === 'home' && !selectedClient && (
                <Stack sx={styles.clientNameGroupTitle}>
                    <Text>1. {translate('board.lineup.choose_club')}:</Text>
                </Stack>
            )}
            {type === 'guest' && !selectedEnemyClient && (
                <Stack sx={styles.clientNameGroupTitle}>
                    <Text>1. {translate('board.lineup.choose_opponent_club')}:</Text>
                </Stack>
            ) */}

            {(type === 'home' && !selectedClient) || (type === 'guest' && !selectedEnemyClient) ? (
                <Stack direction="column" sx={styles.listContainer}>
                    <Box sx={styles.clientTitleContainer}>
                        <Typography sx={styles.teamTitle}>Verein wählen:</Typography>
                    </Box>
                    <Stack
                        value={type === 'home' ? selectedClient?.id || null: selectedEnemyClient?.id || null}
                        variant="fullWidth"
                        direction="column"
                        sx={styles.clientNameGroup}>
                        {filteredClients.map(c =>
                            (<Button
                                key={c.id}
                                onClick={(_e) => {type === 'home' ? setSelectedClient(c) : setSelectedEnemyClient(c);}}
                                sx={styles.client}
                            >
                                <Typography sx={styles.title}>
                                    {c.name}
                                </Typography>
                            </Button>
                            ))}
                    </Stack>
                </Stack>
            ) : null}

            {/* Mein Team > Ausgewählter Verein */}
            {type === 'home' && selectedClient ? (
                <>
                    <Box sx={styles.clientTitleContainer}>
                        <Typography sx={styles.teamTitle}>Verein:</Typography>
                    </Box>
                    <Box sx={styles.selectedClient}>
                        <Box sx={styles.deleteClient} onClick={() => setSelectedClient(null)}><CloseIcon sx={styles.closeIconStyle} /></Box>
                        {/* <Typography sx={styles.miniTitle}>{translate('board.lineup.selected_club')}:</Typography> */}
                        <Stack flexDirection="row" justifyContent="center" alignItems="center">
                            {/* <Box sx={styles.selected}></Box> */}
                            <Typography sx={styles.titleActive}>
                                {selectedClient.name}
                            </Typography>
                        </Stack>
                    </Box>
                    <Box sx={styles.seperator}></Box>
                    <Box sx={styles.teamTitleContainer}>
                        <Typography sx={styles.teamTitle}>Team wählen:</Typography>
                    </Box>
                </>
            ) : null}

            {type === 'guest' && selectedEnemyClient ? (
                <>
                    <Box sx={styles.teamTitleContainer}>
                        <Typography sx={styles.teamTitle}>Verein:</Typography>
                    </Box>
                    <Box sx={styles.selectedClient}>
                        <Box sx={styles.deleteClient} onClick={() => setSelectedEnemyClient(null)}><CloseIcon sx={styles.closeIconStyle} /></Box>
                        <Typography sx={styles.miniTitle}>{translate('board.lineup.selected_club')}:</Typography>
                        <Stack flexDirection="row" justifyContent="center" alignItems="center">
                            {/* <Box sx={styles.selected}></Box> */}
                            <Typography sx={styles.titleActive}>
                                {selectedEnemyClient.name}
                            </Typography>
                        </Stack>
                    </Box>
                    <Box sx={styles.seperator}></Box>
                    <Box sx={styles.teamTitleContainer}>
                        <Typography sx={styles.teamTitle}>Team wählen:</Typography>
                    </Box>
                </>
            ): null}

            {(type === 'home' && selectedClient || type === 'guest' && selectedEnemyClient) && (
                <>
                    {showCreate && (
                        <Create 
                            type={type} 
                            client={type === 'home' ? selectedClient : selectedEnemyClient} 
                            onSave={onSaveTeam} 
                            onClose={() => setShowCreate(false)}
                        />
                    )}
                    <Stack direction="column" sx={styles.listContainer}>
                        {filteredTeams.length > 0 ? (
                            <>
                                {/* type === 'home' && !selectedTeam && (
                                    <Stack sx={styles.teamNameGroupTitle}>
                                        2. {translate('board.lineup.choose_team')}:
                                    </Stack>
                                )}
                                {type === 'guest' && !selectedEnemyTeam && (
                                    <Stack sx={styles.teamNameGroupTitle}>
                                        2. {translate('board.lineup.choose_opponent_team')}:
                                    </Stack>
                                ) */}
                            </>
                        ) : (
                            <Stack sx={styles.notFoundContainer}>
                                <Typography sx={styles.notFoundTitle}>
                                    {translate('board.lineup.teams_not_found')}
                                </Typography>
                                <Button
                                    onClick={() => setShowCreate(true)}
                                    sx={styles.createTeamButton}>
                                    <Typography fontSize={12}>{translate('board.lineup.action.create_team')}</Typography>
                                </Button>
                            </Stack>
                        )}
                     
                        {teamQuery.data?.length > 0 && (
                            <Stack sx={styles.teamNameGroup}>
                                {filteredTeams.map(t => {
                                    // disable teams that are already selected
                                    const isAlreadySelected = (t.id === selectedTeam) || (t.id === selectedEnemyTeam);
                                    return (
                                        isAlreadySelected ?
                                            (
                                                <Box sx={styles.selectedTeam} key={t.id}>
                                                    <Box sx={styles.deleteTeam} onClick={type === 'home' && selectedClient ? (() => setSelectedTeam(null)) : (() => setSelectedEnemyTeam(null))}><CloseIcon sx={styles.closeIconStyle} /></Box>
                                                    {/* <Typography sx={styles.miniTitle}>
                                                        {translate('board.lineup.selected_team')}:
                                                    </Typography> */}
                                                    <Stack flexDirection="row" justifyContent="center" alignItems="center">
                                                        {/* <Box sx={styles.selected}></Box> */}
                                                        <Typography sx={styles.titleActive}>
                                                            {t.name}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            ) : (
                                                <Button
                                                    key={t.id}
                                                    onClick={() => selectTeam(t)}
                                                    sx={styles.client}
                                                >
                                                    {/* <Typography sx={styles.miniTitle}>
                                                        {unTranslated('Team:')}
                                                    </Typography> */}
                                                    <Typography sx={styles.title}>
                                                        {t.name}
                                                    </Typography>
                                                </Button>
                                            )

                                    );})}
                            </Stack>
                        )}
                    </Stack>
                </>
            )}
        </Stack>
    );
};

export default Selector;